import React, { Fragment, useEffect, } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, } from 'reactstrap';
import { format as formatDate, parse as parseDate, eachMonthOfInterval, startOfYear, addYears, getMonth, startOfMonth, getYear, addMonths, startOfDay, endOfDay, } from 'date-fns';
import { toast } from 'react-toastify';
import { transform, fromPairs, maxBy, omit, sumBy, keyBy, sortBy, groupBy, pickBy, get, sum } from 'lodash';
import { useToggle } from 'react-use';
import numeral from 'numeral';
import retry from 'async-retry';

import firebase, { functions } from '../../firebase';
import { getCollectionData, } from '../../shared/firebase';
import { boxSizesForCount, amazonProductSizeTypes, } from '../../shared/config';
import { setSizes, documentNumberHavings, } from '../../shared/models/receivingRequest';
import { idWithTenant, } from '../../shared/util';
import AdminPage from '../hocs/AdminPage';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import QuerySelector from '../QuerySelector';
import ModalButton from '../ModalButton';
import ExportButton from '../ExportButton';
import UseCountDetail from '../UseCountDetail';
import ProgressButton from '../ProgressButton';
import TenantTags from '../TenantTags';

const countTenantUses = functions.httpsCallable('countTenantUses', { timeout: 550000 });
const thisMonth = new Date();
const yearMonthOptions = eachMonthOfInterval({
  start: startOfYear(addYears(thisMonth, -10)),
  end: thisMonth,
}).map(_ => ({ label: formatDate(_, 'yyyy/MM'), value: formatDate(_, 'yyyyMM') }));
const { entries, keys } = Object;
const db = firebase.firestore();
const tenantsRef = db.collection('tenants');
const tagsRef = db.collection('tags');
const useCountsRef = db.collectionGroup('useCounts');

export default AdminPage(function AdminUseCounts (props) {
  const { user } = props;
  const {
    yearMonth: [yearMonth = formatDate(thisMonth, 'yyyyMM')] = [],
  } = useQueryParams();
  const tenants = useCollectionSubscription(tenantsRef);
  const tenantsById = keyBy(tenants, 'id');
  const tags = useCollectionSubscription(tagsRef.orderBy('createdAt'));
  const tagsById = keyBy(tags, 'id');
  const useCounts = useCollectionSubscription(useCountsRef.where('yearMonth', '==', yearMonth), [yearMonth]);
  const sortedUseCounts = sortBy(useCounts, _ => numeral(_.ref.parent.parent.id).value());
  const rowsForExport = () => sortedUseCounts.map((useCount) => {
    const { id, ref, receivingRequestOptionCounts, shippingRequestOptionCounts, combiningCounts, shippingRequestsCount, shippingRequestBoxCounts, toBeShippingRequestBoxCounts = {}, unknownReceivingRequestCount, storageDays, maxStorageVolume, receivingCount, shelvingCounts, extraPickingsCount, unknownInventoriesCount, amazonShipmentItemsCount, } = useCount;
    const tenant = tenantsById[ref.parent.parent.id];

    return {
      tenantId: tenant?.id,
      tenantName: tenant?.name,
      receivingRequestOptionCounts_check: sum(Object.values(receivingRequestOptionCounts.check)),
      ...(
        keys(setSizes).reduce((x, setSize) => {
          const value = sumBy(Object.values(receivingRequestOptionCounts.set), setSize) || 0;
          return {
            ...x,
            [`receivingRequestOptionCounts_set_${setSize}`]: value,
          };
        }, {})
      ),
      receivingRequestOptionCounts_opp: sum(Object.values(receivingRequestOptionCounts.opp || {})),
      receivingRequestOptionCounts_keepDocuments: (receivingRequestOptionCounts.keepDocuments || []).length,
      ...(
        keys(boxSizesForCount).reduce((x, boxSize) => {
          const value = sumBy(Object.values(shippingRequestOptionCounts.opp), boxSize) || 0;
          return {
            ...x,
            [`shippingRequestOptionCounts_opp_${boxSize}`]: value,
          };
        }, {})
      ),
      ...(
        keys(boxSizesForCount).reduce((x, boxSize) => {
          const value = sumBy(Object.values(shippingRequestOptionCounts.puchi), boxSize) || 0;
          return {
            ...x,
            [`shippingRequestOptionCounts_puchi_${boxSize}`]: value,
          };
        }, {})
      ),
      shippingRequestOptionCounts_flyer: Object.values(shippingRequestOptionCounts.flyer).length,
      shippingRequestOptionCounts_print: Object.values(shippingRequestOptionCounts.print).length,
      shippingRequestOptionCounts_slip: Object.values(shippingRequestOptionCounts.slip || {}).length,
      shippingRequestOptionCounts_cashOnDelivery: sumBy(Object.values(shippingRequestOptionCounts.cashOnDelivery), _ => sum(Object.values(_))) || 0,
      ...(
        keys(boxSizesForCount).reduce((x, boxSize) => {
          const value = sum(Object.values(combiningCounts?.[boxSize] || {}));
          return {
            ...x,
            [`combiningCounts_${boxSize}`]: value,
          };
        }, {})
      ),
      shippingRequestsCount,
      ...(
        keys(boxSizesForCount).reduce((x, boxSize) => {
          const value = sumBy(Object.values(shippingRequestBoxCounts), boxSize) || 0;
          return {
            ...x,
            [`shippingRequestBoxCounts_${boxSize}`]: value,
          };
        }, {})
      ),
      ...(
        keys(boxSizesForCount).reduce((x, boxSize) => {
          const value = sumBy(Object.values(toBeShippingRequestBoxCounts), boxSize) || 0;
          return {
            ...x,
            [`toBeShippingRequestBoxCounts_${boxSize}`]: value,
          };
        }, {})
      ),
      unknownReceivingRequestCount: Object.values(unknownReceivingRequestCount).length,
      unknownInventoriesCount: sum(Object.values(unknownInventoriesCount || {})) || 0,
      ...(
        keys(boxSizesForCount).reduce((x, boxSize) => {
          const value = sum(Object.values(storageDays?.[boxSize] || {})) || 0;
          return {
            ...x,
            [`storageDays_${boxSize}`]: value,
          };
        }, {})
      ),
      maxStorageVolume: maxStorageVolume?.totalVolume,
      ...(
        keys(documentNumberHavings).reduce((x, documentNumberHaving) => {
          const value = Object.values(receivingCount?.[documentNumberHaving] || {}).length || 0;
          return {
            ...x,
            [`receivingCount_${documentNumberHaving}`]: value,
          };
        }, {})
      ),
      shelvingCount: sumBy(Object.values(shelvingCounts || {}), _ => sum(Object.values(_))),
      extraPickingsCount: sum(Object.values(extraPickingsCount || {})),
      ...(
        keys(amazonProductSizeTypes).reduce((x, amazonProductSizeType) => {
          const value = sumBy(Object.values(amazonShipmentItemsCount || {}), amazonProductSizeType) || 0;
          return {
            ...x,
            [`amazonShipmentItemsCount_${amazonProductSizeType}`]: value,
          };
        }, {})
      ),
    };
  });
  const onClickCount = async () => {
    await Promise.all(tenants.map(async (tenant) => {
      await retry(async () => {
        await countTenantUses({ tenantId: tenant.id, date: yearMonth.replace(/\d{2}$/, _ => '/' + _) + '/01', });
      }, { retries: 1 });
    }));
    toast.success('完了しました');
  };

  return (
    <div className="admin-use-counts container-fluid">
      <div className="p-4 bg-white my-4">
        <div className="d-flex justify-content-center mb-3">
          <h4>利用状況</h4>
        </div>
        <div className="d-flex justify-content-between align-items-end mb-3">
          <div className="mt-2 d-flex align-items-center flex-wrap">
            <QuerySelector paramName="yearMonth" options={yearMonthOptions} label="月" defaultValue={[formatDate(thisMonth, 'yyyyMM')]} selectorProps={{ isClearable: false }} />
          </div>
          <div className="d-flex gap-1">
            <ProgressButton process={onClickCount}>
              再計算
            </ProgressButton>
            <ExportButton fileName="利用状況.csv" rows={rowsForExport} />
          </div>
        </div>
        <div className="position-relative" style={{ zIndex: 0 }}>
          <table className="table sticky-table table-bordered">
            <thead className="thead-light text-center">
              <tr>
                <th rowSpan={2}>
                  <div>
                    テナント
                  </div>
                  <div className="text-muted small">
                    最終計算日時
                  </div>
                </th>
                <th colSpan={10}>オプション</th>
                <th rowSpan={2}>加工件数</th>
                <th rowSpan={2}>完了出荷依頼数</th>
                <th rowSpan={2}>出荷件数</th>
                <th rowSpan={2}>toB出荷件数</th>
                <th rowSpan={2}>不明件数</th>
                <th rowSpan={2}>不明件数(箱)</th>
                <th rowSpan={2}>保管日数</th>
                <th rowSpan={2}>最大保管量</th>
                <th rowSpan={2}>入荷件数</th>
                <th rowSpan={2}>入庫点数</th>
                <th rowSpan={2}>追加ピッキング数</th>
                <th rowSpan={2}>Amazon納品数</th>
                <th rowSpan={2}></th>
              </tr>
              <tr>
                <th>追加検品</th>
                <th>加工</th>
                <th>OPP梱包(入)</th>
                <th>納品書・明細書確認保管</th>
                <th>OPP梱包(出)</th>
                <th>プチプチ梱包</th>
                <th>チラシ同封</th>
                <th>添付ファイルの印刷同封</th>
                <th>納品書同封</th>
                <th>着払いで出荷</th>
              </tr>
            </thead>
            <tbody>
              {
                sortedUseCounts.map((useCount) => {
                  const { id, ref, receivingRequestOptionCounts, shippingRequestOptionCounts, combiningCounts = {}, shippingRequestsCount, shippingRequestBoxCounts, toBeShippingRequestBoxCounts = {}, unknownReceivingRequestCount, storageDays, maxStorageVolume, receivingCount, shelvingCounts, extraPickingsCount, unknownInventoriesCount, amazonShipmentItemsCount, updatedAt, } = useCount;
                  const tenant = tenantsById[ref.parent.parent.id];
                  const storageVolumeRows = async () => {
                    const storageVolumes = await getCollectionData(tenant?.ref.collection('storageVolumes').where('yearMonth', '==', yearMonth));
                    return storageVolumes?.map((storageVolume) => {
                      const { date, volumes, totalVolume, } = storageVolume;
                      return {
                        date: formatDate(date.toDate(), 'yyyy/MM/dd'),
                        totalVolume: Math.round(totalVolume),
                        ...fromPairs(entries(volumes).map(([productId, volume]) => {
                          return [`${[tenant.id, productId].join('-')}`, Math.round(volume)];
                        })),
                      };
                    });
                  };

                  return (
                    <tr key={idWithTenant(ref)}>
                      <td>
                        <div>
                          {tenant?.id} {tenant?.name}
                        </div>
                        <TenantTags tagIds={tenant?.tagIds} tagsById={tagsById} />
                        <div className="text-muted small">
                          {updatedAt ? formatDate(updatedAt.toDate(), 'yyyy/MM/dd HH:mm') : '-'}
                        </div>
                      </td>
                      <td className="text-right">
                        {numeral(sum(Object.values(receivingRequestOptionCounts.check))).format()}
                      </td>
                      <td className="text-right">
                        {
                          keys(setSizes).map((setSize) => {
                            const value = sumBy(Object.values(receivingRequestOptionCounts.set), setSize);
                            return value ? (
                              <div key={setSize} className="text-nowrap">
                                {setSizes[setSize]?.label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right">
                        {numeral(sum(Object.values(receivingRequestOptionCounts.opp || {}))).format()}
                      </td>
                      <td className="text-right">
                        {numeral((receivingRequestOptionCounts.keepDocuments || []).length).format()}
                      </td>
                      <td className="text-right">
                        {
                          keys(boxSizesForCount).map((boxSize) => {
                            const value = sumBy(Object.values(shippingRequestOptionCounts.opp), boxSize);
                            return value ? (
                              <div key={boxSize} className="text-nowrap">
                                {boxSizesForCount[boxSize].label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right">
                        {
                          keys(boxSizesForCount).map((boxSize) => {
                            const value = sumBy(Object.values(shippingRequestOptionCounts.puchi), boxSize);
                            return value ? (
                              <div key={boxSize} className="text-nowrap">
                                {boxSizesForCount[boxSize].label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right">
                        {numeral(Object.values(shippingRequestOptionCounts.flyer).length).format()}
                      </td>
                      <td className="text-right">
                        {numeral(Object.values(shippingRequestOptionCounts.print).length).format()}
                      </td>
                      <td className="text-right">
                        {numeral(Object.values(shippingRequestOptionCounts.slip || {}).length).format()}
                      </td>
                      <td className="text-right">
                        {numeral(sumBy(Object.values(shippingRequestOptionCounts.cashOnDelivery), _ => sum(Object.values(_)))).format()}
                      </td>
                      <td className="text-right">
                        {
                          keys(boxSizesForCount).map((boxSize) => {
                            const value = sum(Object.values(combiningCounts?.[boxSize] || {}));
                            return value ? (
                              <div key={boxSize} className="text-nowrap">
                                {boxSizesForCount[boxSize].label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right">
                        {numeral(shippingRequestsCount).value()}
                      </td>
                      <td className="text-right">
                        {
                          keys(boxSizesForCount).map((boxSize) => {
                            const value = sumBy(Object.values(shippingRequestBoxCounts), boxSize);
                            return value ? (
                              <div key={boxSize} className="text-nowrap">
                                {boxSizesForCount[boxSize].label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right">
                        {
                          keys(boxSizesForCount).map((boxSize) => {
                            const value = sumBy(Object.values(toBeShippingRequestBoxCounts), boxSize);
                            return value ? (
                              <div key={boxSize} className="text-nowrap">
                                {boxSizesForCount[boxSize].label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right">
                        {numeral(Object.values(unknownReceivingRequestCount).length).format()}
                      </td>
                      <td className="text-right">
                        {numeral(sum(Object.values(unknownInventoriesCount || {}))).format()}
                      </td>
                      <td className="text-right">
                        {
                          keys(boxSizesForCount).map((boxSize) => {
                            const value = sum(Object.values(storageDays?.[boxSize] || {}));
                            return value ? (
                              <div key={boxSize} className="text-nowrap">
                                {boxSizesForCount[boxSize].label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right">
                        {numeral(maxStorageVolume?.totalVolume).format()}
                      </td>
                      <td className="text-right">
                        {
                          keys(documentNumberHavings).map((documentNumberHaving) => {
                            const value = Object.values(receivingCount?.[documentNumberHaving] || {}).length;
                            return value ? (
                              <div key={documentNumberHaving} className="text-nowrap">
                                {documentNumberHavings[documentNumberHaving].label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right">
                        {sumBy(Object.values(shelvingCounts || {}), _ => sum(Object.values(_)))}
                      </td>
                      <td className="text-right">
                        {numeral(sum(Object.values(extraPickingsCount || {}))).format()}
                      </td>
                      <td className="text-right">
                        {
                          keys(amazonProductSizeTypes).map((amazonProductSizeType) => {
                            const value = sumBy(Object.values(amazonShipmentItemsCount || {}), amazonProductSizeType);
                            return value ? (
                              <div key={amazonProductSizeType} className="text-nowrap">
                                {amazonProductSizeTypes[amazonProductSizeType].label}: {numeral(value).format()}
                              </div>
                            ) : null;
                          })
                        }
                      </td>
                      <td className="text-right text-nowrap">
                        <div>
                          <ModalButton
                            modalProps={{ style: { minWidth: 1000 } }}
                            label="明細"
                            title="明細"
                            content={_ => <UseCountDetail useCount={useCount} tenant={tenant} />}
                          />
                        </div>
                        <div className="mt-1">
                          <ExportButton fileName="保管量.csv" label="保管量" rows={storageVolumeRows} headersOrder={[_ => ({ date: -2, totalVolume: -1, })[_] ?? parseInt(_.split('-')[1], 10), 'asc']} />
                        </div>
                      </td>
                    </tr>
                  );
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});
